import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import _, { isEmpty } from 'lodash';
import { AuthContext, TokenStatus, User } from '@/context/auth.context';
import { authorizeRole } from '@/constant/global/global.constant';
import { getRole } from '@/constant/users/user.constant';
import { SETTINGS_KEY } from '@/utils/settings';

const TERMINAL_AUTH_ERRORS = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'missing_refresh_token',
  'invalid_refresh_token',
]);

const isTerminalAuthError = (err: unknown): boolean => {
  const maybeErr = err as { error?: string; message?: string };
  const errorCode = maybeErr?.error?.toLowerCase?.() ?? '';
  const message = maybeErr?.message?.toLowerCase?.() ?? '';

  if (TERMINAL_AUTH_ERRORS.has(errorCode)) return true;
  return Array.from(TERMINAL_AUTH_ERRORS).some((code) =>
    message.includes(code),
  );
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Wipes all auth-related storage while preserving persisted UI settings
 * (theme, sidebar state, timezone preference etc.).
 */
const clearAuthStorage = (): void => {
  const settings = localStorage.getItem(SETTINGS_KEY);
  localStorage.clear();
  sessionStorage.clear();
  if (settings) localStorage.setItem(SETTINGS_KEY, settings);
};

/**
 * AuthProvider — single source of truth for auth state.
 *
 * Responsibilities:
 *  1. Silently fetch + validate the Auth0 access token
 *  2. Persist the token to localStorage for downstream utilities (getRole etc.)
 *  3. Invalidate the session (clear storage + logout) on any token failure
 *  4. Role-check: redirect to /forbidden if user lacks PORTAL.READ
 *  5. Expose { token, tokenStatus, user, setUser } via AuthContext
 */
export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently, logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  // ── Token state ─────────────────────────────────────────────────────────
  const [token, setToken] = useState<string | null>(null);
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>('pending');
  // Prevent double-logout in React StrictMode / concurrent renders
  const logoutFired = useRef(false);

  // ── User state (owned here — useUser reads it via context) ───────────────
  const [user, setUser] = useState<User | null>(null);

  // ── Session invalidation ─────────────────────────────────────────────────
  const invalidateSession = useCallback(
    (reason: string) => {
      if (logoutFired.current) return;
      logoutFired.current = true;
      console.warn('[AuthProvider] Session invalidated →', reason);
      clearAuthStorage();
      logout({ logoutParams: { returnTo: window.location.origin } });
    },
    [logout],
  );

  // ── Token validation (runs once when Auth0 confirms active session) ──────
  useEffect(() => {
    if (!isAuthenticated) return;

    const validateToken = async () => {
      try {
        const accessToken = await getAccessTokenSilently();

        if (isEmpty(accessToken)) {
          setTokenStatus('invalid');
          invalidateSession('Token returned empty');
          return;
        }

        localStorage.setItem('access_token', accessToken);
        setToken(accessToken);
        setTokenStatus('valid');
      } catch (err) {
        const cachedToken = localStorage.getItem('access_token');

        if (!isTerminalAuthError(err) && !isEmpty(cachedToken)) {
          console.warn(
            '[AuthProvider] Silent token failed, using cached token once:',
            err,
          );
          setToken(cachedToken);
          setTokenStatus('valid');
          return;
        }

        setTokenStatus('invalid');
        invalidateSession(`Token fetch error: ${String(err)}`);
      }
    };

    validateToken();
    // invalidateSession is stable (useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // ── Role check (runs once token is confirmed valid) ───────────────────────
  useEffect(() => {
    if (!token) return;
    const userHasHubAccess = _.includes(authorizeRole.PORTAL.READ, getRole());
    if (!userHasHubAccess && location.pathname !== '/forbidden') {
      navigate('/forbidden', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <AuthContext.Provider value={{ token, tokenStatus, user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
